import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InternalPageHeader } from "@/components/InternalPageHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Ship, Plus, Upload, Trash2, Edit2, X, Check, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShipmentsPage({ userRole, onLogout }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savingShipment, setSavingShipment] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    material: "",
    quantity: "",
    unit: "SQM",
    status: "In Transit",
    etd: "",
    eta: "",
    notes: ""
  });

  useEffect(() => {
    fetchShipments();
  }, []);

  const fetchShipments = async () => {
    try {
      const response = await axios.get(`${API}/shipments`);
      setShipments(response.data);
    } catch (error) {
      toast.error("Failed to load shipments");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);

    try {
      const response = await axios.post(`${API}/shipments/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        toast.success(response.data.message);
        fetchShipments();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Upload failed";
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingShipment) return;
    
    if (!formData.material) {
      toast.error("Material is required");
      return;
    }

    setSavingShipment(true);
    try {
      if (editingId) {
        await axios.put(`${API}/shipments/${editingId}`, {
          ...formData,
          quantity: parseFloat(formData.quantity) || 0
        });
        toast.success("Shipment updated");
      } else {
        await axios.post(`${API}/shipments`, {
          ...formData,
          quantity: parseFloat(formData.quantity) || 0
        });
        toast.success("Shipment added");
      }
      
      resetForm();
      fetchShipments();
    } catch (error) {
      toast.error("Failed to save shipment");
    } finally {
      setSavingShipment(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this shipment?")) return;
    
    try {
      await axios.delete(`${API}/shipments/${id}`);
      toast.success("Shipment deleted");
      fetchShipments();
    } catch (error) {
      toast.error("Failed to delete shipment");
    }
  };

  const startEdit = (shipment) => {
    setFormData({
      material: shipment.material,
      quantity: shipment.quantity.toString(),
      unit: shipment.unit || "SQM",
      status: shipment.status || "In Transit",
      etd: shipment.etd || "",
      eta: shipment.eta || "",
      notes: shipment.notes || ""
    });
    setEditingId(shipment.id);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      material: "",
      quantity: "",
      unit: "SQM",
      status: "In Transit",
      etd: "",
      eta: "",
      notes: ""
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered": return "#16a34a";
      case "in transit": return "#3b82f6";
      case "customs": return "#f59e0b";
      case "delayed": return "#DC2626";
      default: return "#64748b";
    }
  };

  const getStatusBg = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered": return "bg-green-50";
      case "in transit": return "bg-blue-50";
      case "customs": return "bg-amber-50";
      case "delayed": return "bg-red-50";
      default: return "bg-gray-50";
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] select-none w-full overflow-x-hidden">
      <InternalPageHeader
        title="SHIPMENTS"
        subtitle="Track incoming stock and expected delivery flow"
        onBack={() => navigate("/stock")}
        backTestId="shipments-back-button"
        leading={<Ship className="h-5 w-5 sm:h-6 sm:w-6 text-[#DC2626]" />}
        actions={userRole === "admin" && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="sr-only"
                  id="shipment-upload"
                />
                <label htmlFor="shipment-upload" className="cursor-pointer">
                  <Button
                    data-testid="upload-shipments-button"
                    disabled={uploading}
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/10 pointer-events-none"
                    asChild
                  >
                    <span>
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? "UPLOADING..." : "UPLOAD"}
                    </span>
                  </Button>
                </label>
                <Button
                  data-testid="add-shipment-button"
                  onClick={() => setShowAddForm(true)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  ADD
                </Button>
              </>
            )}
      />

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Add/Edit Form */}
        {showAddForm && userRole === "admin" && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border-l-4 border-[#DC2626]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="heading-font text-xl text-[#09090b]">
                {editingId ? "Edit Shipment" : "Add New Shipment"}
              </h2>
              <Button variant="ghost" size="sm" onClick={resetForm}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium mb-1 block">Material *</Label>
                <Input
                  data-testid="shipment-material-input"
                  placeholder="Product/Material name"
                  value={formData.material}
                  onChange={(e) => setFormData({...formData, material: e.target.value})}
                  className="h-10"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1 block">Quantity</Label>
                <Input
                  data-testid="shipment-quantity-input"
                  type="number"
                  placeholder="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                  className="h-10"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1 block">Unit</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({...formData, unit: v})}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SQM">SQM</SelectItem>
                    <SelectItem value="PCS">PCS</SelectItem>
                    <SelectItem value="ROLLS">ROLLS</SelectItem>
                    <SelectItem value="BOXES">BOXES</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1 block">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="In Transit">In Transit</SelectItem>
                    <SelectItem value="Customs">Customs</SelectItem>
                    <SelectItem value="Delivered">Delivered</SelectItem>
                    <SelectItem value="Delayed">Delayed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1 block">ETD (Departure)</Label>
                <Input
                  data-testid="shipment-etd-input"
                  type="text"
                  placeholder="e.g., 15 Jan 2025"
                  value={formData.etd}
                  onChange={(e) => setFormData({...formData, etd: e.target.value})}
                  className="h-10"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-1 block">ETA (Arrival)</Label>
                <Input
                  data-testid="shipment-eta-input"
                  type="text"
                  placeholder="e.g., 30 Jan 2025"
                  value={formData.eta}
                  onChange={(e) => setFormData({...formData, eta: e.target.value})}
                  className="h-10"
                />
              </div>
              
              <div className="md:col-span-2 lg:col-span-3">
                <Label className="text-sm font-medium mb-1 block">Notes</Label>
                <Input
                  data-testid="shipment-notes-input"
                  placeholder="Additional notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="h-10"
                />
              </div>
              
              <div className="md:col-span-2 lg:col-span-3 flex gap-2">
                <Button
                  type="submit"
                  data-testid="shipment-submit-button"
                  disabled={savingShipment}
                  className="bg-[#DC2626] hover:bg-[#DC2626]/90 text-white disabled:opacity-60"
                >
                  <Check className="h-4 w-4 mr-2" />
                  {savingShipment ? "Saving..." : `${editingId ? "Update" : "Add"} Shipment`}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Shipments List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#DC2626] border-t-transparent"></div>
            <p className="mt-4 text-[#64748b] font-medium">Loading shipments...</p>
          </div>
        ) : shipments.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-[#f1f5f9] rounded-full mx-auto flex items-center justify-center mb-4">
              <Ship className="h-10 w-10 text-[#94a3b8]" />
            </div>
            <h3 className="heading-font text-2xl mb-2 text-[#09090b]">No Upcoming Shipments</h3>
            <p className="text-[#64748b] mb-6 text-sm">
              {userRole === "admin"
                ? "Track incoming stock by adding shipments here."
                : "No shipments have been scheduled yet."}
            </p>
            {userRole === "admin" && (
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <button
                  onClick={() => setShowAddForm(true)}
                  data-testid="empty-add-shipment-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#09090b] text-white text-sm font-semibold rounded-lg hover:bg-[#1f1f23] transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Add Shipment
                </button>
                <label htmlFor="shipment-upload" className="cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-[#E5E7EB] text-[#374151] text-sm font-semibold rounded-lg hover:bg-[#f9fafb] transition-colors" data-testid="empty-upload-shipment-btn">
                  <Upload className="h-4 w-4" />
                  Upload File
                </label>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-[#64748b] font-medium mb-4">
              <span className="number-font text-[#09090b] text-base">{shipments.length}</span> shipments
            </p>
            
            {shipments.map((shipment) => (
              <div
                key={shipment.id}
                data-testid={`shipment-item-${shipment.id}`}
                className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 p-4 border-l-4`}
                style={{ borderLeftColor: getStatusColor(shipment.status) }}
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="heading-font text-lg text-[#09090b]">
                        {shipment.material}
                      </h3>
                      <span 
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBg(shipment.status)}`}
                        style={{ color: getStatusColor(shipment.status) }}
                      >
                        {shipment.status}
                      </span>
                    </div>
                    {shipment.notes && (
                      <p className="text-sm text-[#64748b]">{shipment.notes}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <p className="text-xs text-[#64748b] uppercase font-medium">Quantity</p>
                      <p className="number-font text-xl text-[#09090b]">
                        {shipment.quantity.toLocaleString()}
                      </p>
                      <p className="text-xs text-[#64748b]">{shipment.unit}</p>
                    </div>
                    
                    {shipment.etd && (
                      <div className="text-center">
                        <p className="text-xs text-[#64748b] uppercase font-medium">ETD</p>
                        <p className="text-sm text-[#09090b] font-medium">{shipment.etd}</p>
                      </div>
                    )}
                    
                    {shipment.eta && (
                      <div className="text-center">
                        <p className="text-xs text-[#64748b] uppercase font-medium">ETA</p>
                        <p className="text-sm text-[#16a34a] font-medium">{shipment.eta}</p>
                      </div>
                    )}
                    
                    {userRole === "admin" && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEdit(shipment)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit2 className="h-4 w-4 text-[#64748b]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(shipment.id)}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4 text-[#DC2626]" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* File Format Info */}
        {userRole === "admin" && (
          <div className="mt-8 bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div className="flex items-start gap-3">
              <FileSpreadsheet className="h-5 w-5 text-[#64748b] mt-0.5" />
              <div className="text-sm text-[#64748b]">
                <p className="font-medium text-[#09090b] mb-1">Upload File Format</p>
                <p>Supported: CSV, Excel (.xlsx, .xls)</p>
                <p>Columns: Material, Quantity, Unit, Status, ETD, ETA, Notes</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
